import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { TextAlign } from '@syncfusion/ej2-react-grids'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import { InsurancePlan } from '@dataTypes/insurance.types'
import {
  checkPatientEligibility,
  setVerifiedOnInsurancePlanById,
} from '@store/actions/insurance.action'
import { useAppDispatch } from '@store/hooks'
import { AppDispatch } from '@store/index'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { GlobalLoadingOptions } from '@context/GlobalLoadingContext'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'

import { logger } from '@utils/safeLogger'

interface PlanDetailsProps {
  insurancePlan?: InsurancePlan
  outlined?: boolean
}

const handleVerifyInsurancePlanClick = async (
  insurancePlan: any,
  dispatch: AppDispatch,
  showLoading: (options: GlobalLoadingOptions) => void,
  hideLoading: (container: string) => void,
  showToast: (toast: NotificationToastData) => void,
): Promise<void> => {
  showLoading({
    container: '.plan-details-container',
    hideElements: ['.insurance-container > .tab-content'],
  })
  const formData = {
    patientId: insurancePlan.patient_member_id,
    patientDob: insurancePlan.patient_dob,
  }
  // Remove this once we have production ready DentalXChange API
  // Mock service is used to simulate the third-party system while keeping our application aligned with their data structure.
  const dataForMockService = {
    ...formData,
    patientDetails: {
      memberId: insurancePlan.patient_member_id,
      firstName: insurancePlan.patient_first_name,
      lastName: insurancePlan.patient_last_name,
      dateOfBirth: insurancePlan.patient_dob.split('T')[0],
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
      },
    },
    existingPatientPayers: [],
  }
  await dispatch(checkPatientEligibility(dataForMockService))
    .then(async (state) => {
      const planActive =
        state?.payload?.response?.eligibilities?.[0]?.insurance?.planStatus ===
        'Active'
          ? true
          : false
      if (planActive) {
        await dispatch(
          setVerifiedOnInsurancePlanById(insurancePlan.insurance_plan_id),
        )
        setTimeout(() => {
          hideLoading('.plan-details-container')
          showToast(
            createSuccessToast(
              'Insurance plan verified successfully',
              'Insurance plan has been verified successfully.',
            ),
          )
        }, 2000)
      } else {
        setTimeout(() => {
          hideLoading('.plan-details-container')
          showToast(
            createFailedToast(
              'Insurance plan not verified',
              'Insurance plan not verified. Please try again.',
            ),
          )
        }, 2000)
      }
    })
    .catch((error) => {
      logger.error('Error occurred:', error)
      setTimeout(() => {
        hideLoading('.plan-details-container')
        showToast(
          createFailedToast(
            'Insurance plan not verified',
            'Insurance plan not verified. Please try again.',
          ),
        )
      }, 2000)
    })
}

const PlanDetails: React.FC<PlanDetailsProps> = ({
  insurancePlan,
  outlined = false,
}) => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const verifiedInsuranceTemplate = ({
    verified_on,
  }: {
    verified_on: string
  }): JSX.Element => (
    <div className="template-cell">
      {verified_on}
      {verified_on !== '-' && (
        <span
          className="verifiedon-verify"
          onClick={() =>
            handleVerifyInsurancePlanClick(
              insurancePlan,
              dispatch,
              showLoading,
              hideLoading,
              showToast,
            )
          }
        >
          Verify
        </span>
      )}
    </div>
  )

  const columnsInsurancePlanDetails = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '16px',
      },
      {
        field: 'patient_fullname',
        headerText: 'Patient name',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'patient_dob',
        headerText: 'Date of birth',
        width: '110px',
        textAlign: 'Left' as TextAlign,
      },
      {
        field: 'group_number',
        headerText: 'Group number',
        width: '96px',
        textAlign: 'Left' as TextAlign,
        template: ({ group_number }: { group_number: string }): JSX.Element => (
          <div className="template-cell">{group_number || '-'}</div>
        ),
      },
      {
        field: 'start_date',
        headerText: 'Start date',
        textAlign: 'Left' as TextAlign,
        template: ({ start_date }: { start_date: string }): JSX.Element => (
          <div className="template-cell">{start_date || '-'}</div>
        ),
      },
      {
        field: 'end_date',
        headerText: 'End date',
        textAlign: 'Left' as TextAlign,
        template: ({ end_date }: { end_date: string }): JSX.Element => (
          <div className="template-cell">{end_date || '-'}</div>
        ),
      },
      {
        field: 'effective_date',
        headerText: 'Effective date',
        textAlign: 'Left' as TextAlign,
        template: ({
          effective_date,
        }: {
          effective_date: string
        }): JSX.Element => (
          <div className="template-cell">{effective_date || '-'}</div>
        ),
      },
      {
        field: 'verified_on',
        headerText: 'Verified on',
        textAlign: 'Left' as TextAlign,
        template: verifiedInsuranceTemplate,
      },
    ].map((column) => ({
      ...column,
    }))
  }, [insurancePlan])

  return (
    <div className="section-insurance-details">
      {insurancePlan ? (
        <Row className="mt-2 mb-2">
          <Col md={12}>
            <div className="grid-container">
              <DataGridComponent
                data={[insurancePlan]}
                columns={columnsInsurancePlanDetails}
                rowHeight={36}
                allowPaging={false}
                allowSorting={false}
              />
            </div>
          </Col>
        </Row>
      ) : (
        <InsuranceEmptyCard
          iconClass="mgc mgc_shield_fill"
          emptyTitle="No plan details found"
          description="You don't have any plan details on record yet."
          outlined={outlined}
        />
      )}
    </div>
  )
}

export default PlanDetails
